import React from 'react';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import { childrenClass, railItemClass, subItemClass } from '../railClasses';
import { unitPages } from './contentPages';
import { useRailLink } from './RailLink';
import { Toggle } from './TreeNode';
import useLessonSections from './useLessonSections';
import { TreeLesson, TreeUnit } from './useProgramTree';

// Rows that share their line with a chevron can't be w-full: they take what the gutter leaves.
const treeRowClass = (active: boolean) => cn(
  railItemClass(active),
  'tw-w-auto tw-min-w-0 tw-flex-1 tw-items-baseline',
);

const SectionsSkeleton = () => (
  <div aria-hidden="true" className="tw-px-2 tw-py-1">
    {['tw-w-28', 'tw-w-36', 'tw-w-24'].map(width => (
      <div
        key={width}
        className={`tw-my-1.5 tw-h-3 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton-subtle ${width}`}
      />
    ))}
  </div>
);

interface SectionListProps {
  lesson: TreeLesson;
  programId: number;
  sectionId?: number;
}

// A lesson's sections, loaded when the lesson opens. Leaves: each goes to its Text page.
const SectionList = ({ lesson, programId, sectionId }: SectionListProps) => {
  const RailLink = useRailLink();
  const { data: sections = [], isLoading } = useLessonSections(lesson.id, true);

  return (
    <div className={childrenClass}>
      {isLoading && <SectionsSkeleton />}
      {sections.map(section => (
        <RailLink
          key={section.id}
          active={section.id === sectionId}
          className={subItemClass}
          page={{
            href: Routes.edit_admin_section_path(section.id, { lesson_id: lesson.id, program_id: programId }),
            label: section.full_title,
          }}
        >
          <span className="tw-min-w-0 tw-leading-snug">{section.full_title}</span>
        </RailLink>
      ))}
    </div>
  );
};

interface LessonItemProps {
  current: boolean;
  expanded: boolean;
  lesson: TreeLesson;
  onToggle: () => void;
  programId: number;
  sectionId?: number;
}

// The lesson row: chevron opens its sections; the title goes to the lesson's home, Sections.
const LessonItem = ({ current, expanded, lesson, onToggle, programId, sectionId }: LessonItemProps) => {
  const RailLink = useRailLink();

  return (
    <div>
      <div className="tw-flex tw-items-start">
        <Toggle expanded={expanded} label={lesson.title} onToggle={onToggle} />
        <RailLink
          ancestor={current}
          className={treeRowClass}
          page={{
            href: Routes.admin_lesson_sections_path(lesson.id, { program_id: programId }),
            label: lesson.title,
            to: `/content/programs/${programId}/lessons/${lesson.id}/sections`,
          }}
        >
          {lesson.title_number && (
            <>
              <span className="tw-shrink-0 tw-text-xs tw-text-spa-tertiary">{`Lesson ${lesson.title_number}`}</span>
              {' '}
            </>
          )}
          <span className="tw-min-w-0 tw-leading-snug">{lesson.title}</span>
        </RailLink>
      </div>
      {expanded && <SectionList lesson={lesson} programId={programId} sectionId={current ? sectionId : undefined} />}
    </div>
  );
};

interface UnitGroupProps {
  current: boolean;
  expanded: boolean;
  expandedLessons: Set<number>;
  lessonId?: number;
  onToggle: () => void;
  onToggleLesson: (lessonId: number) => void;
  programId: number;
  sectionId?: number;
  unit: TreeUnit;
}

// The unit row: chevron opens its lessons; the title goes to the unit's home, Edit Unit (a router
// link where that page is in the SPA). The unitless group (id 0) has no page, so its title is plain.
const UnitGroup = ({
  current, expanded, expandedLessons, lessonId, onToggle, onToggleLesson, programId, sectionId, unit,
}: UnitGroupProps) => {
  const RailLink = useRailLink();
  const unitClass = (active: boolean) => cn(treeRowClass(active), 'tw-font-semibold');
  const label = (
    <>
      {unit.display_number && (
        <>
          <span className="tw-shrink-0 tw-text-xs tw-font-normal tw-text-spa-tertiary">
            {`Unit ${unit.display_number}`}
          </span>
          {' '}
        </>
      )}
      <span className="tw-min-w-0 tw-leading-snug">{unit.title}</span>
    </>
  );

  return (
    <div>
      <div className="tw-flex tw-items-start">
        <Toggle expanded={expanded} label={unit.title} onToggle={onToggle} />
        {unit.id === 0
          ? <span className={unitClass(false)}>{label}</span>
          : (
            <RailLink
              ancestor={current}
              className={unitClass}
              page={{ ...unitPages(unit.id, programId)[0], label: unit.title }}
            >
              {label}
            </RailLink>
          )}
      </div>
      {expanded && (
        <div className={childrenClass}>
          {unit.lessons.map(lesson => (
            <LessonItem
              key={lesson.id}
              current={lesson.id === lessonId}
              expanded={expandedLessons.has(lesson.id)}
              lesson={lesson}
              programId={programId}
              sectionId={sectionId}
              onToggle={() => onToggleLesson(lesson.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { LessonItem };
export default UnitGroup;
